import { z } from 'zod';

/**
 * URL search-parameter schema for the `/billing` route.
 *
 * Mirrors `features/settings/search-schema.ts` — including its `?dialog=` name
 * and its `.catch(undefined)` idiom — rather than inventing a one-off flag, so
 * the two routes' modal-in-the-URL convention stays one convention.
 *
 * `tab` is the active page tab (default `overview`). Upgrade CTAs that navigate
 * here pass `{ tab: 'plans' }`, since the pricing grid has its own tab.
 *
 * Today the only dialog reachable by URL is `keep-subscription`: the sidebar
 * plan card is rendered on every page but the lifecycle dialog lives on
 * `/billing`, so its "Keep my plan" / "Don't pause" CTA has to navigate AND say
 * which dialog to open on arrival. Every other billing dialog is opened by a
 * control that is already on the page and needs no deep link.
 *
 * `.catch(undefined)` so a stale bookmark (a value removed from this list later,
 * or a hand-edited URL) degrades to "no dialog" instead of
 * throwing a ZodError out of `validateSearch` and taking the whole page down.
 */
export const BILLING_TABS = ['overview', 'plans', 'invoices'] as const;
export type BillingTab = (typeof BILLING_TABS)[number];

export const BILLING_DIALOGS = ['keep-subscription'] as const;
export type BillingDialog = (typeof BILLING_DIALOGS)[number];

export const billingSearchSchema = z.object({
  tab: z.enum(BILLING_TABS).optional().catch(undefined),
  dialog: z.enum(BILLING_DIALOGS).optional().catch(undefined),
});

export type BillingSearch = z.infer<typeof billingSearchSchema>;
